// src/oura/components/fixes/TaskParts.tsx
//
// The controls and blocks a task shows in both Fixes views and in the fix
// drawer: its status, its due date, and the pages it is about. One copy of
// each, so a status set in one place reads the same everywhere.

import { ChevronDownIcon } from "lucide-react";
import type { AuditIssue } from "../../audit";
import type { TaskStatus, TrackerTask } from "../../tracker";
import { cn } from "../../lib/cn";
import { FOCUS_RING, TYPE } from "@oura/ui";
import { useWritesPaused } from "@context/maintenance";
import { SETTABLE, TASK_STATUS, dayDate } from "./model";

/**
 * The status as a native select: a pill with the status word.
 *
 * Native because it is never clipped. The week tables scroll sideways and a
 * drawer scrolls inside itself, and a native list opens above both. A task
 * the data marked blocked shows "Blocked" as its value, which cannot be picked
 * again: blocked is set by the data, not by hand.
 */
export function StatusSelect({
    value,
    onChange,
    label,
    className,
}: {
    value: TaskStatus;
    onChange: (s: TaskStatus) => void;
    /** Accessible name, e.g. "Status of Implement structured data". */
    label: string;
    className?: string;
}) {
    const writesPaused = useWritesPaused();
    const options = SETTABLE.indexOf(value) >= 0 ? SETTABLE : SETTABLE.concat(value);
    return (
        <span className={cn("relative inline-flex shrink-0", className)}>
            <select
                value={value}
                disabled={writesPaused}
                aria-label={label}
                onChange={(e) => {
                    const next = options.find((o) => o === e.target.value);
                    if (next && next !== value) onChange(next);
                }}
                className={cn(
                    TYPE.tc,
                    "h-[30px] cursor-pointer appearance-none rounded-full bg-white pl-3 pr-7 font-medium text-gray-900 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50",
                    FOCUS_RING,
                )}
            >
                {options.map((o) => (
                    <option key={o} value={o} disabled={SETTABLE.indexOf(o) < 0}>
                        {TASK_STATUS[o].label}
                    </option>
                ))}
            </select>
            <ChevronDownIcon aria-hidden size={12} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
        </span>
    );
}

/**
 * A task's due date, editable in place.
 *
 * The value shown is always real: the backend falls back to the block's
 * milestone date, computed over 20 working days with Malaysian holidays
 * (ourteam-backend#4477), and says which it is through `targetDateSource`. A
 * block-derived date is muted, an operator-set one is not, so nobody has to
 * wonder whether a date was chosen or inherited.
 *
 * Clearing sends `""`, which the backend reads as "go back to the block's
 * date" rather than pinning today's answer as if someone had chosen it.
 */
export function TargetDate({ task, onSet }: { task: TrackerTask; onSet: (date: string) => void }) {
    const writesPaused = useWritesPaused();
    const value = task.targetDate ? task.targetDate.slice(0, 10) : "";
    const fromBlock = task.targetDateSource !== "task";
    return (
        // The date reads the way every other date in Oura does ("Tue 29 Sep")
        // rather than in the browser's numeric form, and a chevron marks it
        // as editable: a bare date stamp looked read-only, and nobody clicked
        // it. The native picker sits over the whole control, invisible, so a
        // click anywhere opens the browser's own calendar and the keyboard
        // reaches a real date field.
        <span
            title={fromBlock ? "From this block's milestone date. Set one to override it." : "Set for this task"}
            className={cn(
                TYPE.tc,
                "group relative inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-transparent py-1 pl-1.5 pr-1 tabular-nums",
                "transition-colors hover:border-gray-200 hover:bg-gray-50",
                "focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500",
                // Inherited dates are lighter than chosen ones, but not the
                // gray-400 they were: that read as disabled.
                fromBlock ? "text-gray-600" : "text-gray-900",
            )}
        >
            <span aria-hidden>{value ? dayDate(value) : "Set a date"}</span>
            <ChevronDownIcon aria-hidden className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" />
            <input
                type="date"
                value={value}
                disabled={writesPaused}
                onChange={(e) => onSet(e.target.value)}
                aria-label={`Target date for ${task.action}`}
                className={cn(
                    "absolute inset-0 h-full w-full cursor-pointer opacity-0",
                    "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0",
                    "[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full",
                    "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
                )}
            />
        </span>
    );
}

/**
 * Only the first ten are listed. A finding with seventy pages is a Site
 * issues job, not a plan row, and the link to it is right there.
 */
const PAGES_SHOWN = 10;

/**
 * The pages a task is actually about (#3636), and what fixes them.
 *
 * The count on the "fixing" line has always been the interesting half of the
 * row and the unreachable one: "54 pages" is a number the reader cannot act on.
 * `affectedPages` has been on the contract since ourteam-backend#4353, already
 * filtered by the BE to pages that still fail.
 *
 * Content only: the week table frames it in grey, the drawer in a white block.
 */
export function AffectedPages({
    task,
    issue,
    onOpenIssue,
}: {
    task: TrackerTask;
    issue?: AuditIssue;
    onOpenIssue?: (key: string) => void;
}) {
    const pages = task.affectedPages ?? [];
    const shown = pages.slice(0, PAGES_SHOWN);
    const rest = pages.length - shown.length;
    return (
        <div>
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <p className={cn(TYPE.lab, "m-0")}>Affected pages</p>
                <span className={cn(TYPE.tm, "text-gray-400")}>
                    {pages.length} {pages.length === 1 ? "page" : "pages"}
                </span>
            </div>
            <ul className="list-plain m-0 space-y-2 p-0">
                {shown.map((pg) => (
                    <li
                        key={pg.url}
                        className="flex items-start justify-between gap-4 rounded-lg bg-white px-3 py-2 ring-1 ring-inset ring-black/[0.06]"
                    >
                        <span className="min-w-0">
                            <span className={cn(TYPE.tm, "block truncate font-medium text-gray-900")}>{pg.title || pg.url}</span>
                            <a
                                href={pg.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className={cn(TYPE.tm, "mt-0.5 block truncate text-blue-600 hover:underline")}
                            >
                                {pg.url}
                            </a>
                        </span>
                        {/* The check's own column value (a status code, a word
                            count), which is the whole reason to look at the list. */}
                        {pg.meta && <span className={cn(TYPE.tm, "shrink-0 whitespace-nowrap text-gray-500")}>{pg.meta}</span>}
                    </li>
                ))}
            </ul>
            {rest > 0 && (
                <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                    And {rest} more.{" "}
                    {issue && onOpenIssue ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenIssue(issue.key);
                            }}
                            className={cn("rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                        >
                            See them all in Site issues
                        </button>
                    ) : (
                        "See them all in Site issues."
                    )}
                </p>
            )}
            {issue && issue.fix.length > 0 && (
                <div className="mt-4 border-t border-gray-200 pt-3">
                    <p className={cn(TYPE.lab, "m-0 mb-1.5")}>What fixes it</p>
                    <ol className="list-plain m-0 space-y-1 p-0">
                        {issue.fix.map((step, i) => (
                            <li key={i} className={cn(TYPE.tm, "flex gap-2 leading-relaxed text-gray-600")}>
                                <span className="shrink-0 text-gray-400">{i + 1}.</span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
